import { useCallback, useEffect, useRef, useState } from "react";

export type Loaded<T> =
  | { status: "loading"; data: undefined; error: undefined }
  | { status: "ready"; data: T; error: undefined }
  | { status: "error"; data: undefined; error: unknown };

const LOADING = { status: "loading", data: undefined, error: undefined } as const;

/**
 * Loads one value and reloads it when `key` changes. An answer is only ever shown for the key it was asked
 * for: a slow answer to an earlier key is dropped, and on a key change the old value is gone at once.
 */
export function useLoad<T>(
  load: () => Promise<T>,
  key: string,
): Loaded<T> & { reload: () => void; set: (data: T) => void } {
  const loadRef = useRef(load);
  loadRef.current = load;
  const [state, setState] = useState<{ key: string; value: Loaded<T> }>({ key, value: LOADING });
  const [round, setRound] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `round` is the reason to load the same key again
  useEffect(() => {
    let alive = true;
    setState({ key, value: LOADING });
    loadRef.current().then(
      (data) => {
        if (alive) setState({ key, value: { status: "ready", data, error: undefined } });
      },
      (error: unknown) => {
        if (alive) setState({ key, value: { status: "error", data: undefined, error } });
      },
    );
    return () => {
      alive = false;
    };
  }, [key, round]);

  const reload = useCallback(() => setRound((r) => r + 1), []);
  const set = useCallback(
    (data: T) => setState((prev) => ({ key: prev.key, value: { status: "ready", data, error: undefined } })),
    [],
  );
  const value = state.key === key ? state.value : LOADING;
  return { ...value, reload, set };
}

export interface Paged<T> {
  items: T[];
  /** `more` and `more-error` keep the items already shown */
  status: "loading" | "ready" | "error" | "more" | "more-error";
  error: unknown;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
  setItems: (change: (items: T[]) => T[]) => void;
}

interface PagedState<T> {
  key: string;
  items: T[];
  next: string | null;
  status: Paged<T>["status"];
  error: unknown;
}

const NO_ITEMS: never[] = [];

/** A cursor-paged list: the first page loads when `key` changes, `loadMore` appends the next one. */
export function usePaged<T>(
  fetchPage: (cursor: string | undefined) => Promise<{ items: T[]; nextCursor: string | null }>,
  key: string,
): Paged<T> {
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const seq = useRef(0);
  const inFlight = useRef(false);
  const [state, setState] = useState<PagedState<T>>({
    key,
    items: NO_ITEMS,
    next: null,
    status: "loading",
    error: null,
  });
  const [round, setRound] = useState(0);

  const run = useCallback((forKey: string, cursor: string | undefined) => {
    const mine = ++seq.current;
    inFlight.current = true;
    setState((prev) =>
      cursor
        ? { ...prev, status: "more", error: null }
        : { key: forKey, items: NO_ITEMS, next: null, status: "loading", error: null },
    );
    fetchRef.current(cursor).then(
      (page) => {
        if (seq.current !== mine) return;
        inFlight.current = false;
        setState((prev) => ({
          key: forKey,
          items: cursor ? [...prev.items, ...page.items] : page.items,
          next: page.nextCursor,
          status: "ready",
          error: null,
        }));
      },
      (error: unknown) => {
        if (seq.current !== mine) return;
        inFlight.current = false;
        setState((prev) => ({ ...prev, status: cursor ? "more-error" : "error", error }));
      },
    );
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `round` is the reason to load the same key again
  useEffect(() => {
    run(key, undefined);
    return () => {
      seq.current++;
      inFlight.current = false;
    };
  }, [key, round, run]);

  const current: PagedState<T> =
    state.key === key ? state : { key, items: NO_ITEMS, next: null, status: "loading", error: null };
  const currentRef = useRef(current);
  currentRef.current = current;

  const loadMore = useCallback(() => {
    const s = currentRef.current;
    if (!inFlight.current && (s.status === "ready" || s.status === "more-error") && s.next)
      run(s.key, s.next);
  }, [run]);
  const reload = useCallback(() => setRound((r) => r + 1), []);
  const setItems = useCallback(
    (change: (items: T[]) => T[]) => setState((prev) => ({ ...prev, items: change(prev.items) })),
    [],
  );

  return {
    items: current.items,
    status: current.status,
    error: current.error,
    hasMore: current.next !== null,
    loadMore,
    reload,
    setItems,
  };
}
